import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Badge from '../Badge.jsx';
import Tooltip from '../Tooltip.jsx';
import { entityListTagsClass, entityListTagsMeasureClass } from './entityListClasses.js';

function EntityListTagBadge({ tag, className = '', onClick }) {
  return (
    <Badge
      variant={tag.variant ?? 'lightBlue'}
      size="small"
      onClick={onClick ?? tag.onClick}
      clickable={typeof (onClick ?? tag.onClick) === 'function'}
      className={['shrink-0 whitespace-nowrap', className].filter(Boolean).join(' ')}
    >
      {tag.label}
    </Badge>
  );
}

/**
 * Greedy fit: reserves space for the `+N` badge whenever any tag remains hidden.
 * `overflowWidth` is measured for the worst-case digit count (`+tags.length`), so the
 * reservation never under-estimates and visible tags are never clipped.
 */
function computeVisibleTagCount(tagWidths, overflowWidth, gap, availableWidth) {
  if (!tagWidths.length || availableWidth <= 0) return 0;

  let usedWidth = 0;
  let visibleCount = 0;

  for (let index = 0; index < tagWidths.length; index += 1) {
    const tagWidth = tagWidths[index];
    const hiddenCount = tagWidths.length - index - 1;
    const gapBeforeTag = visibleCount > 0 ? gap : 0;
    const overflowReserve = hiddenCount > 0 ? gap + overflowWidth : 0;
    const nextWidth = usedWidth + gapBeforeTag + tagWidth + overflowReserve;

    if (nextWidth <= availableWidth + 0.5) {
      usedWidth += gapBeforeTag + tagWidth;
      visibleCount += 1;
      continue;
    }

    break;
  }

  return visibleCount;
}

/**
 * Footer tags for {@link EntityList}. Secondary meta keeps layout priority; tags use
 * remaining width and collapse into `+N` when they no longer fit (Figma 16508:6751).
 */
export function EntityListTags({ tags = [], className = '' }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(tags.length);

  const measureVisibleTags = useCallback(() => {
    const container = containerRef.current;
    const measureRow = measureRef.current;

    if (!container || !measureRow || tags.length === 0) {
      setVisibleCount((prev) => (prev === tags.length ? prev : tags.length));
      return;
    }

    const availableWidth = container.clientWidth;
    if (availableWidth <= 0) return;

    const gap = Number.parseFloat(getComputedStyle(container).columnGap) || 0;

    const tagWidths = Array.from(
      measureRow.querySelectorAll('[data-measure-tag]'),
      (element) => element.getBoundingClientRect().width,
    );

    const overflowEl = measureRow.querySelector('[data-measure-overflow]');
    const overflowWidth = overflowEl ? overflowEl.getBoundingClientRect().width : 0;

    const nextVisibleCount = computeVisibleTagCount(tagWidths, overflowWidth, gap, availableWidth);
    setVisibleCount((prev) => (prev === nextVisibleCount ? prev : nextVisibleCount));
  }, [tags]);

  // Re-measure whenever the tags change (count or labels alter pill widths).
  useLayoutEffect(() => {
    measureVisibleTags();
  }, [measureVisibleTags]);

  // Mount the ResizeObserver once; it calls the latest measure fn via ref.
  // Safe because the container is `flex-1 min-w-0 overflow-hidden`, so its width
  // is parent-driven and never changes with its own content (no feedback loop).
  const measureRefFn = useRef(measureVisibleTags);
  measureRefFn.current = measureVisibleTags;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    let frameId = 0;
    const scheduleMeasure = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        frameId = 0;
        measureRefFn.current();
      });
    };

    const resizeObserver = new ResizeObserver(scheduleMeasure);
    resizeObserver.observe(container);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, []);

  if (!tags.length) return null;

  const visibleTags = tags.slice(0, visibleCount);
  const hiddenTags = tags.slice(visibleCount);
  const overflowCount = hiddenTags.length;

  const overflowTooltip =
    overflowCount > 0 ? (
      <ul className="m-0 max-h-48 list-disc space-y-1 overflow-y-auto pl-4 text-left text-sm leading-snug">
        {hiddenTags.map((tag, index) => (
          <li key={tag.id ?? tag.key ?? `${tag.label}-hidden-${index}`}>{tag.label}</li>
        ))}
      </ul>
    ) : null;

  return (
    <div
      ref={containerRef}
      className={[entityListTagsClass, className].filter(Boolean).join(' ')}
    >
      <div ref={measureRef} className={entityListTagsMeasureClass} aria-hidden>
        {tags.map((tag, index) => (
          <span key={`measure-tag-${index}`} data-measure-tag="" className="inline-flex shrink-0">
            <EntityListTagBadge tag={tag} />
          </span>
        ))}
        <span data-measure-overflow="" className="inline-flex shrink-0">
          <EntityListTagBadge tag={{ label: `+${tags.length}` }} />
        </span>
      </div>

      {visibleTags.map((tag, index) => (
        <EntityListTagBadge
          key={tag.id ?? tag.key ?? `${tag.label}-${index}`}
          tag={tag}
        />
      ))}

      {overflowCount > 0 ? (
        <Tooltip content={overflowTooltip} position="bottom" align="end" size="compact">
          <EntityListTagBadge tag={{ label: `+${overflowCount}` }} />
        </Tooltip>
      ) : null}
    </div>
  );
}
